import {useReportUserVisibleError} from '@github-ui/copilot-browser-latency/use-report-user-visible-error'
import {userErrorCodeFromRelayFailure} from '@github-ui/copilot-browser-latency/user-visible-errors'
import type {RelayConnectionProgress} from '@github-ui/ahp-relay/relay-connection-runtime'
import {relayFailureDisposition, type RelayFailure} from '@github-ui/ahp-relay/relay-failure'
import {announce} from '@github-ui/aria-live'
import {LoadingMessage} from '@github-ui/agent-sessions-interface/components/LoadingMessage'
import {Banner} from '@primer/react'
import {useEffect, useEffectEvent} from 'react'

import {
  getSessionResumeErrorReason,
  useSessionResumeErrorTelemetry,
  type SessionResumeErrorContext,
} from '../../utils/session-resume-error-metric'
import styles from './SessionResumeNotice.module.css'

export interface SessionResumeNoticeProps {
  id?: string
  /** Session identity reported to Hydro each time this notice shows an error. */
  errorTelemetry: SessionResumeErrorContext
  pending: boolean
  error?: string
  failure?: RelayFailure
  newConversationHref?: string
  progress?: RelayConnectionProgress
  presentationPhase?: 'waking' | 'connecting'
  slow?: boolean
  canRetry?: boolean
  onRetry?: () => void
  visible?: boolean
}

interface SessionResumeNoticeContent {
  title: string
  description?: string
}

function failureTitle(failure: RelayFailure | undefined): string {
  if (!failure) return 'The session could not reconnect'
  switch (failure.kind) {
    case 'http':
      switch (failure.code) {
        case 'environment_compute_missing':
        case 'environment_compute_terminal':
          return "This session's environment is no longer available"
        case 'environment_authentication_unavailable':
          return 'Environment authentication is unavailable'
        case 'environment_unreachable':
          return "This session's environment cannot be reached right now"
        case 'copilot_license_required':
          return 'A Copilot license is required to reconnect this session'
        default:
          return 'The session could not reconnect'
      }
    case 'protocol-version':
      return 'This session requires a newer compatible version of Copilot'
    case 'rpc':
      switch (relayFailureDisposition(failure).presentation) {
        case 'runtime-update':
          return 'This session requires a newer compatible version of Copilot'
        case 'session-unavailable':
          return 'This session can no longer be resumed'
        default:
          return 'The session could not reconnect'
      }
    case 'wake-timeout':
      return "This session's environment did not become ready in time"
    case 'provisioning':
      return 'The session could not finish preparing its environment'
    default:
      return 'The session could not reconnect'
  }
}

function failureDescription(failure: RelayFailure | undefined): string {
  if (!failure) return 'Try reconnecting to continue working in this session.'

  const presentation = relayFailureDisposition(failure).presentation
  switch (presentation) {
    case 'compute-unavailable':
      return 'This environment cannot be reconnected. Start a new conversation to continue working.'
    case 'session-unavailable':
      return 'The live session is no longer available. Start a new conversation to continue working.'
    case 'runtime-update':
      return 'Reopen this session after a compatible version of Copilot is available.'
    case 'license-required':
      return 'Check that Copilot access is available, then reopen this session.'
    case 'environment-unreachable':
      return 'Check the environment and try reconnecting.'
    case 'wake-timeout':
    case 'generic':
      return 'Try reconnecting to continue working in this session.'
  }
}

export function getSessionResumeNoticeContent({
  pending,
  error,
  failure,
}: Omit<SessionResumeNoticeProps, 'id' | 'onRetry' | 'errorTelemetry'>): SessionResumeNoticeContent | undefined {
  if (error || failure) {
    return {
      title: failureTitle(failure),
      description: failureDescription(failure),
    }
  }
  if (!pending) return undefined
  const message = 'Connecting…'
  return {
    title: message,
  }
}

export function SessionResumeNotice(props: SessionResumeNoticeProps) {
  const visible = props.visible ?? true
  const content = getSessionResumeNoticeContent(props)
  const title = content?.title
  const description = content?.description
  const hasError = Boolean(props.error || props.failure)
  const errorReason = hasError ? getSessionResumeErrorReason(props.failure) : undefined
  const failureDisposition = props.failure ? relayFailureDisposition(props.failure) : undefined
  const showNewConversationAction =
    (failureDisposition?.presentation === 'compute-unavailable' ||
      failureDisposition?.presentation === 'session-unavailable') &&
    Boolean(props.newConversationHref)
  const showRetryAction =
    !showNewConversationAction &&
    failureDisposition?.manualRetry !== false &&
    props.canRetry !== false &&
    Boolean(props.onRetry)

  useEffect(() => {
    if (!title) return
    announce([title, description].filter(Boolean).join('. '), {assertive: Boolean(props.error || props.failure)})
  }, [description, props.error, props.failure, title])

  // Keyed on the reason so a re-render of the same error is not counted twice, while an
  // error that clears (for example during a retry) and returns is counted again.
  const sendErrorTelemetry = useSessionResumeErrorTelemetry()
  const reportError = useEffectEvent((reason: string) => sendErrorTelemetry(props.errorTelemetry, reason))
  useEffect(() => {
    if (errorReason) reportError(errorReason)
  }, [errorReason])
  useReportUserVisibleError(
    visible && hasError && content
      ? props.failure
        ? userErrorCodeFromRelayFailure(props.failure)
        : 'session_connect_failed'
      : undefined,
    {identity: errorReason},
  )

  if (!content) return null

  return (
    <div id={props.id} data-testid="session-resume-notice">
      {!hasError && props.pending ? (
        <div className={styles.Progress}>
          <LoadingMessage message={content.title} />
          {content.description ? <p className={styles.ProgressDescription}>{content.description}</p> : null}
        </div>
      ) : (
        <Banner
          variant="critical"
          title={content.title}
          description={content.description}
          primaryAction={
            showNewConversationAction ? (
              <Banner.PrimaryAction as="a" href={props.newConversationHref} target="_blank" rel="noopener noreferrer">
                Start a new conversation (opens in a new tab)
              </Banner.PrimaryAction>
            ) : showRetryAction ? (
              <Banner.PrimaryAction onClick={props.onRetry}>Retry connection</Banner.PrimaryAction>
            ) : undefined
          }
        />
      )}
    </div>
  )
}

SessionResumeNotice.displayName = 'SessionResumeNotice'
